/**
 * Build the `rows` half of a sheet-scan request, plus the index that routes the
 * answer back into the wizard's own edit state (PROD-1279 D27, plan 03 §D.2).
 *
 * The `rowId` sent to the extractor is the wizard's own row key — the transport
 * flow's `AggregatedDeliveryRow.key` / `MilkReceiptGroup.key`, and the service
 * flow's `productionFillRowKey`, the latter NAMESPACED by its production record
 * (see `serviceScanWireId`). Either way the index hands the flow the exact
 * setter arguments it already uses, so a value coming back against a `rowId`
 * needs no matching pass.
 *
 * `parentRowId` carries the one structural fact the server enforces: a row that
 * hangs off another one (a milk SKU under its drop, a second grade band under
 * its item) shares the parent's single temperature and therefore never carries
 * a temperature of its own.
 *
 * Pure — no React, no fetch. Types come from the modules that own them so an
 * additive change there (e.g. a new `ProductionEdits` field) cannot drift a
 * hand-copied shape here.
 */

import type { ProductionFillGradeRow, ProductionFillMealtime } from '@/api/cafeteria-production'
import {
  type AggregatedDeliveryRow,
  type MilkReceiptGroup,
  tempCheckRequired,
} from '@/lib/cafeteria/aggregate-deliveries'
import { productionFillRowKey } from '@/lib/cafeteria/format'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'

/** One row of the scan request's `rows` array — the wire shape frozen in D24. */
export interface ScanRowWire {
  rowId: string
  name: string
  /** Grade legend ("K-8", "All grades") or vendor + age ranges; null when the row has none. */
  groupLabel: string | null
  /** The mealtime card's label, so duplicate item names across cards stay tellable apart. */
  mealtimeLabel: string
  temperatureClass: 'hot' | 'chilled' | 'ambient' | null
  /** Ordered quantity — the extractor's sanity ceiling for a count. */
  planned: number | null
  /** Set on a row that shares its parent's temperature (milk SKU, 2nd..Nth grade band). */
  parentRowId: string | null
}

/** Where a transport `rowId` writes back to: an item row, or a milk group's shared temperature. */
export interface TransportScanTarget {
  kind: 'row' | 'milkGroup'
  key: string
  /**
   * False for a shelf-stable (AMBIENT) row, which takes no reading at all: the
   * grid renders `N/A — room temp` there with no control, so a temperature
   * written against it is invisible, unclearable, and a fabricated entry on a
   * HACCP record. The printed sheet still boxes the cell and the extractor
   * still answers for it, so the refusal has to live on this side.
   */
  tempOffered: boolean
}

/** Where a service `rowId` writes back to, in `updateRow` / `updateTemp` argument order. */
export interface ServiceScanTarget {
  productionRecordId: string
  deliverableKey: string
  /**
   * `productionFillRowKey` for a grade row; `''` for a deliverable with no
   * grade rows. Deliberately NOT namespaced — this is the key the flow's own
   * `updateRow` takes, and the record it belongs to travels beside it.
   */
  rowKey: string
  planned: number
  /** True for the row that carries the deliverable's single serving temperature. */
  isItemRow: boolean
}

/** The grid's HACCP classes are upper-case on the wire; the extractor speaks lower-case. */
function lowerTemperatureClass(value: 'HOT' | 'CHILLED' | 'AMBIENT' | null): ScanRowWire['temperatureClass'] {
  switch (value) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}

/** Vendor + age ranges under an item — the same legend `rowDetail` renders in the wizard. */
function deliveryRowLegend(row: AggregatedDeliveryRow): string | null {
  const detail = [row.vendorName, ...row.ageRangeNames].filter(Boolean).join(', ')
  return detail === '' ? null : detail
}

/** The grade ladder the service grid shows under an item (D6). */
export function serviceGradeLabel(row: ProductionFillGradeRow): string {
  return row.groupLabel ?? row.gradeGroupName ?? row.rosterName ?? 'All grades'
}

/**
 * The wire id for a service row: its production record, then its row key.
 *
 * A `deliverableKey` is the item label plus its HACCP class, built PER
 * PRODUCTION RECORD (`buildProductionFillDeliverables`), and the grade bands are the same
 * org-level USDA bands on every record — so two mealtimes that serve an item of
 * the same name and HACCP class mint the SAME `productionFillRowKey`. The
 * Production Records day task scans the WHOLE service day in one request, so
 * that collision is the ordinary case (`0% Milk` at breakfast and at lunch):
 * without this namespace the request carries a duplicate `rowId`, the server's
 * body validator rejects the whole scan, and if it did not, breakfast's numbers
 * would be written onto the lunch record.
 *
 * The namespace lives on the WIRE only. `ServiceScanTarget.rowKey` stays the
 * bare `productionFillRowKey` the flow's own `updateRow` takes.
 */
export function serviceScanWireId(productionRecordId: string, rowKey: string): string {
  return `${productionRecordId}::${rowKey}`
}

/**
 * Transport rows: one per aggregated delivery row, plus one parent + N sub-rows
 * per milk group. The milk parent carries the drop's single chilled reading and
 * the SKU sub-rows carry the counts — one physical stack of cartons, one
 * temperature, however many SKUs and service days it covers.
 */
export function buildTransportScanRows(
  groups: ReadonlyArray<{ mealtime: Mealtime; rows: readonly AggregatedDeliveryRow[] }>,
  milkGroups: readonly MilkReceiptGroup[],
): { rows: ScanRowWire[]; index: Map<string, TransportScanTarget> } {
  const rows: ScanRowWire[] = []
  const index = new Map<string, TransportScanTarget>()

  for (const group of groups) {
    const mealtimeLabel = MEALTIME_LABELS[group.mealtime]
    for (const row of group.rows) {
      rows.push({
        rowId: row.key,
        name: row.label,
        groupLabel: deliveryRowLegend(row),
        mealtimeLabel,
        temperatureClass: lowerTemperatureClass(row.temperatureClass),
        planned: row.plannedTotal,
        parentRowId: null,
      })
      index.set(row.key, { kind: 'row', key: row.key, tempOffered: tempCheckRequired(row.temperatureClass) })
    }
  }

  for (const group of milkGroups) {
    rows.push({
      rowId: group.key,
      name: group.vendorName ? `Milk (${group.vendorName})` : 'Milk',
      groupLabel: null,
      mealtimeLabel: 'Milk',
      temperatureClass: 'chilled',
      planned: null,
      parentRowId: null,
    })
    index.set(group.key, { kind: 'milkGroup', key: group.key, tempOffered: true })
    for (const row of group.rows) {
      rows.push({
        rowId: row.key,
        name: row.label,
        groupLabel: deliveryRowLegend(row),
        mealtimeLabel: 'Milk',
        temperatureClass: 'chilled',
        planned: row.plannedTotal,
        parentRowId: group.key,
      })
      index.set(row.key, { kind: 'row', key: row.key, tempOffered: tempCheckRequired(row.temperatureClass) })
    }
  }

  return { rows, index }
}

/**
 * Service rows: per mealtime, per deliverable, one row per grade band. Grade
 * band 0's counts live ON the item row (D6), so band 0 IS the parent and carries
 * the deliverable's single serving temperature; bands 2..N point at it. A
 * deliverable with no grade bands still emits its item row, otherwise its
 * temperature would be unscannable.
 */
export function buildServiceScanRows(mealtimes: readonly ProductionFillMealtime[]): {
  rows: ScanRowWire[]
  index: Map<string, ServiceScanTarget>
} {
  const rows: ScanRowWire[] = []
  const index = new Map<string, ServiceScanTarget>()

  for (const mealtime of mealtimes) {
    const mealtimeLabel = MEALTIME_LABELS[mealtime.mealtime]
    for (const deliverable of mealtime.deliverables) {
      const temperatureClass = lowerTemperatureClass(deliverable.temperatureClass)
      const gradeRows = deliverable.gradeRows

      if (gradeRows.length === 0) {
        const wireId = serviceScanWireId(mealtime.productionRecordId, deliverable.deliverableKey)
        rows.push({
          rowId: wireId,
          name: deliverable.label,
          groupLabel: null,
          mealtimeLabel,
          temperatureClass,
          planned: null,
          parentRowId: null,
        })
        index.set(wireId, {
          productionRecordId: mealtime.productionRecordId,
          deliverableKey: deliverable.deliverableKey,
          rowKey: '',
          planned: 0,
          isItemRow: true,
        })
        continue
      }

      let parentWireId: string | null = null
      gradeRows.forEach((gradeRow, i) => {
        const rowKey = productionFillRowKey(deliverable.deliverableKey, gradeRow)
        const wireId = serviceScanWireId(mealtime.productionRecordId, rowKey)
        const isItemRow = i === 0
        rows.push({
          rowId: wireId,
          name: isItemRow ? deliverable.label : serviceGradeLabel(gradeRow),
          groupLabel: serviceGradeLabel(gradeRow),
          mealtimeLabel,
          temperatureClass,
          planned: gradeRow.planned,
          parentRowId: isItemRow ? null : parentWireId,
        })
        index.set(wireId, {
          productionRecordId: mealtime.productionRecordId,
          deliverableKey: deliverable.deliverableKey,
          rowKey,
          planned: gradeRow.planned,
          isItemRow,
        })
        if (isItemRow) {
          parentWireId = wireId
        }
      })
    }
  }

  return { rows, index }
}
